import { useState, useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import {
  useFormState,
  useFormApi,
  useArrayFieldApi,
  useArrayFieldItemApi,
  FormState,
  FormApi,
  ArrayFieldItemApi,
  ArrayFieldApi,
} from 'informed';

const DEFAULT_TIMEOUT = 300;
const RENDER_DELAY_MS = 10;
// Types

type AnimatedRelevantParams = {
  formState?: FormState;
  formApi?: FormApi;
  arrayFieldApi?: ArrayFieldApi;
  arrayFieldItemApi?: ArrayFieldItemApi;
};

interface AnimatedRelevantProps {
  when: (p?: AnimatedRelevantParams) => boolean;
  children: React.ReactNode;
  timeout?: number;
}

/**
 * AnimatedRelevant component provides smooth fade-in/fade-out transitions for conditionally rendered content using Material UI Collapse
 */
export default function AnimatedRelevant({
  when,
  children,
  timeout = DEFAULT_TIMEOUT,
}: AnimatedRelevantProps) {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(false);
  const [conditionMet, setConditionMet] = useState<boolean>(false);
  const formState = useFormState();
  const formApi = useFormApi();
  const arrayFieldApi = useArrayFieldApi();
  const arrayFieldItemApi = useArrayFieldItemApi();
  // figure out if the condition has been met!
  useEffect(() => {
    setConditionMet(
      when({
        formApi,
        formState,
        arrayFieldApi,
        arrayFieldItemApi,
      }),
    );
  }, [when, formState, formApi, arrayFieldApi, arrayFieldItemApi]);

  useEffect(() => {
    if (conditionMet) {
      // Show: first render, then animate in
      setShouldRender(true);
      const timer = setTimeout(() => setIsVisible(true), RENDER_DELAY_MS);
      return () => clearTimeout(timer);
    } else {
      // Hide: first animate out, then unmount
      setIsVisible(false);
      const timer = setTimeout(() => setShouldRender(false), timeout);
      return () => clearTimeout(timer);
    }
  }, [conditionMet, timeout]);

  // Don't render anything if condition is not met and animation is complete
  if (!shouldRender) {
    return null;
  }

  return (
    <Collapse
      in={isVisible}
      timeout={timeout}
      appear={true}
      unmountOnExit={false}
    >
      {children}
    </Collapse>
  );
}
